import React, { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Users,
  Globe,
  Shield,
  CheckCircle,
} from "lucide-react";
import { Button } from "src/vibe-code/rcm-v2/components/ui/button";
import { Badge } from "src/vibe-code/rcm-v2/components/ui/badge";
import { Card } from "src/vibe-code/rcm-v2/components/ui/card";
import type { Dealer, Employee } from "src/vibe-code/rcm-v2/types";
import {
  formatDealerType,
  formatEmployeeRole,
  formatEmployeeStatus,
} from "src/vibe-code/rcm-v2/types";
import { formatDistanceToNow } from "date-fns";

interface DealersTableProps {
  dealers: Dealer[];
  employees: Employee[];
  onDealerClick: (dealer: Dealer) => void;
  onEmployeeClick: (employee: Employee) => void;
}

const DEALER_TYPE_VARIANTS: Record<
  string,
  "outline" | "secondary" | "default"
> = {
  INDEPENDENT_DEALERSHIP: "outline",
  MOBILE_HOME_MILITIA: "secondary",
};

export function DealersTable({
  dealers,
  employees,
  onDealerClick,
  onEmployeeClick,
}: DealersTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (dealerId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(dealerId)) {
      newExpanded.delete(dealerId);
    } else {
      newExpanded.add(dealerId);
    }
    setExpandedRows(newExpanded);
  };

  const getDealerEmployees = (dealerId: string) => {
    return employees.filter((employee) => employee.dealer.id === dealerId);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-2">Dealers</h2>
        <p className="text-muted-foreground">
          Manage your dealer relationships and associated employees
        </p>
      </div>

      <Card className="overflow-hidden shadow-custom-md">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="text-left p-4 font-semibold">Dealer</th>
                <th className="text-left p-4 font-semibold">Website</th>
                <th className="text-left p-4 font-semibold">Type</th>
                <th className="text-left p-4 font-semibold">Employees</th>
                <th className="text-left p-4 font-semibold">Last Updated</th>
                <th className="text-left p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dealers.map((dealer) => {
                const dealerEmployees = getDealerEmployees(dealer.id);
                const isExpanded = expandedRows.has(dealer.id);
                return (
                  <React.Fragment key={dealer.id}>
                    <tr className="table-row">
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRow(dealer.id)}
                            className="p-1"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                          <div className="max-w-[400px]">
                            <button
                              onClick={() => onDealerClick(dealer)}
                              className="font-medium text-primary hover:text-primary-dark transition-colors"
                            >
                              {dealer.name}
                            </button>
                            <div className="flex items-center space-x-4 text-sm text-muted-foreground mt-1">
                              <div className="flex items-center space-x-1">
                                <Phone className="w-3 h-3" />
                                <span>{dealer.phone_number}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Mail className="w-3 h-3" />
                                <span>{dealer.email}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        {dealer.website_url ? (
                          <a
                            href={dealer.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-primary hover:text-primary-dark text-sm"
                          >
                            <Globe className="w-3 h-3" />
                            <span className="truncate max-w-32">
                              {dealer.website_url
                                .replace("https://", "")
                                .replace("http://", "")}
                            </span>
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            —
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge
                          variant={
                            DEALER_TYPE_VARIANTS[dealer.dealer_type] ??
                            "default"
                          }
                          className="text-xs"
                        >
                          {formatDealerType(dealer.dealer_type)}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <Badge variant="secondary">
                            {dealerEmployees.length}
                          </Badge>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {dealer.updated_at &&
                            !isNaN(new Date(dealer.updated_at).getTime())
                              ? formatDistanceToNow(
                                  new Date(dealer.updated_at),
                                  {
                                    addSuffix: true,
                                  },
                                )
                              : "Never updated"}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDealerClick(dealer)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>

                    {/* Expanded row showing employees */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <div className="bg-muted/30 border-t">
                            <div className="p-4">
                              <h4 className="font-medium mb-3 text-sm text-muted-foreground">
                                Associated Employees ({dealerEmployees.length})
                              </h4>
                              {dealerEmployees.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {dealerEmployees.map((employee) => (
                                    <div
                                      key={employee.id}
                                      className="bg-card p-3 rounded-lg border shadow-custom-sm cursor-pointer hover:shadow-custom-md transition-shadow"
                                      onClick={() => onEmployeeClick(employee)}
                                    >
                                      <div className="flex items-start justify-between mb-2">
                                        <div>
                                          <div className="flex items-center space-x-2">
                                            <h5 className="font-medium text-sm">
                                              {employee.first_name}{" "}
                                              {employee.last_name}
                                            </h5>
                                            {employee.role ===
                                              "GENERAL_MANAGER" && (
                                              <div title="GM Permissions">
                                                <Shield className="w-3 h-3 text-accent" />
                                              </div>
                                            )}
                                          </div>
                                          <p className="text-xs text-muted-foreground">
                                            {formatEmployeeRole(employee.role)}
                                          </p>
                                        </div>
                                        <Badge
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {formatEmployeeStatus(
                                            employee.status,
                                          )}
                                        </Badge>
                                      </div>
                                      <div className="space-y-1">
                                        <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                          <Phone className="w-3 h-3" />
                                          <span>{employee.phone_number}</span>
                                        </div>
                                        <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                          <Mail className="w-3 h-3" />
                                          <span className="truncate">
                                            {employee.email}
                                          </span>
                                        </div>
                                        {employee.birthday && (
                                          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                            <Calendar className="w-3 h-3" />
                                            <span>
                                              Birthday:{" "}
                                              {new Date(
                                                employee.birthday,
                                              ).toLocaleDateString()}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  No employees associated with this dealer.
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
